import React from 'react';

import { Box, Typography } from '@mui/material';

import { classicRewardsMargin, formatMarginPercent } from './societeRewardMargin';

interface Props {
  /** AUD cost-of-goods-sold for the package. `null` renders both values as "n/a" regardless of points/copay. */
  cogsAud: number | null;
  /** Formula-side base-stay points: the reward's last-computed `computed_points_base`. */
  formulaPoints: number;
  /** Formula-side base-stay AUD co-pay: the AUD price row's server-computed base copay. */
  formulaCopayAud: number | null;
  /** `false` when the server-computed copay field isn't available (absent `undefined`, not an explicit `null`) — the formula value renders "n/a" regardless of `formulaCopayAud`/`formulaPoints`. */
  formulaCopayAvailable: boolean;
  /** Override/current base-stay points (override input taking precedence over the live value). */
  overridePoints: number;
  /** Override/current base-stay AUD co-pay (override input taking precedence over the live value). */
  overrideCopayAud: number | null;
  /** Flash config `minMarginPercent` (0-100): the override value below it renders in the warning color. `null`: no warning. */
  minMarginPercent: number | null;
  /** When `false`, renders only the override value as a single line (today's behaviour). When `true`, renders both as "formula: … · override: …". */
  showBoth: boolean;
}

/**
 * Live "Reward margin" preview line shown inside the override dialog. Recomputes from whatever
 * points/co-pay the caller currently has in play (override input taking precedence over the live
 * value), so it updates as the admin types — before anything is submitted.
 *
 * Shows a single value (the override/current margin, as before) until there's something
 * meaningful to compare it against: once the package is already overridden, or the admin has
 * edited the inputs that feed this comparison, it shows the FORMULA value (computed from the
 * reward's last-computed points/copay) alongside the OVERRIDE value side by side.
 */
export default function ClassicRewardsMarginLine({
  cogsAud,
  formulaPoints,
  formulaCopayAud,
  formulaCopayAvailable,
  overridePoints,
  overrideCopayAud,
  minMarginPercent,
  showBoth,
}: Props) {
  const overrideMargin = classicRewardsMargin({ cogsAud, points: overridePoints, copayAud: overrideCopayAud });
  const formulaMargin = formulaCopayAvailable
    ? classicRewardsMargin({ cogsAud, points: formulaPoints, copayAud: formulaCopayAud })
    : null;
  const belowThreshold =
    overrideMargin !== null && minMarginPercent !== null && overrideMargin * 100 < minMarginPercent;

  if (!showBoth) {
    return (
      <Typography variant="body2" color={belowThreshold ? 'warning.main' : 'text.secondary'}>
        Reward margin: {formatMarginPercent(overrideMargin)}
      </Typography>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary">
      Reward margin — formula: {formatMarginPercent(formulaMargin)} ·{' '}
      <Box component="span" sx={{ color: belowThreshold ? 'warning.main' : 'text.secondary' }}>
        override: {formatMarginPercent(overrideMargin)}
      </Box>
    </Typography>
  );
}
